import { Button, CircularProgress, TextField, Tooltip } from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import EditIcon from "@material-ui/icons/Edit";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import ReviewsIcon from "@material-ui/icons/RateReview";
import Autocomplete from "@material-ui/lab/Autocomplete/Autocomplete";
import React, { useEffect, useState } from "react";
import { shallowEqual, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import {
  Card,
  CardBody,
  CardHeader,
  CardHeaderToolbar,
} from "../../../../_metronic/_partials/controls";
import { getCustomers } from "../../../../api/customer";
import {
  deleteOfferHeader,
  duplicateOfferHeader,
  getOfferHeaders,
} from "../../../../api/offerHeader";
import { getOfferStatus } from "../../../../api/offerStatus";
import { getOfferStatusHistory } from "../../../../api/offerStatusHistory";
import { alertError, alertSuccess } from "../../../../utils/logger";
import ConfirmDialog from "../../../components/dialogs/ConfirmDialog";
import FiltersCard from "../../../components/filters/Filter";
import Table, {
  buttonsStyle,
  getCurrentTablePage,
  getOfferStatusAppearance,
  offerStatusFormatter,
} from "../../../components/tables/table";
import OfferRevisionTableDialog from "../../../components/dialogs/OfferRevisionTableDialog";
import DuplicarOfertaRevisionDialog from "../../../components/dialogs/DuplicarOfertaRevisionDialog";
import { OFFER_STATUS, ROLES } from "../../../../utils/constants";
import { getOfferDateRange, OFFER_DATE_PERIODS, toInputDate } from "../../../../utils/offerDatePeriods";
import { getLatestOfferStatusMovement, getOfferDashboardType, indexOfferStatusHistory } from "../../../../utils/offerDashboardMetrics";
import "./offers-page.scss";

function getData(offerHeaders, customers, offerStatus) {
  let data = [];
  for (let i = 0; i < offerHeaders.length; ++i) {
    const elem = {};
    const offerHeader = offerHeaders[i];

    elem.estado = offerStatus?.find((x) => x.id === offerHeader?.estado_id);
    elem.numOferta = offerHeader?.num_oferta;
    elem.cliente = customers?.find(
      (x) => x.id === offerHeader?.cliente_id
    )?.nombre;
    elem.localizacion = offerHeader?.localizacion;
    elem.referencia = offerHeader?.referencia;
    elem.revisionNum = offerHeader?.revision_num;
    elem.id = i;

    elem.key = {
      id: offerHeader?.id,
      revisionNum: offerHeader?.revision_num,
      revisionNums: offerHeader?.revision_nums,
      offerStatus: elem?.estado,
    };

    data = data.concat(elem);
  }
  return data;
}

function agruparOfertas(ofertas) {
  const ofertasAgrupadas = [];
  const ofertasPorId = new Map();

  for (let oferta of ofertas) {
    let ofertaCopia = { ...oferta };

    if (!ofertasPorId.has(ofertaCopia.id)) {
      ofertaCopia.revision_nums = false;
      ofertasAgrupadas.push(ofertaCopia);
      ofertasPorId.set(ofertaCopia.id, ofertaCopia);
    } else {
      const ofertaAgrupada = ofertasPorId.get(ofertaCopia.id);
      if (ofertaAgrupada.revision_num < ofertaCopia.revision_num) {
        Object.assign(ofertaAgrupada, ofertaCopia);
      }
      ofertaAgrupada.revision_nums = true;
    }
  }

  return ofertasAgrupadas;
}

const FILTERS_STORAGE_KEY = "offers-list-filters";
const initialFilters = {
  clienteId: "",
  estadoId: "",
  dashboardMetric: "",
  periodo: "",
  fechaDesde: "",
  fechaHasta: "",
};
const dashboardMetricTitles = {
  total: "Ofertas realizadas",
  approved: "Ofertas aprobadas",
  rejected: "Ofertas rechazadas",
  pending: "Ofertas pendientes",
};

function readSavedFilters(dashboardFilters) {
  if (dashboardFilters) return { ...initialFilters, ...dashboardFilters };
  try {
    const saved = JSON.parse(sessionStorage.getItem(FILTERS_STORAGE_KEY));
    if (!saved) return { ...initialFilters };
    return {
      ...initialFilters,
      ...saved,
      periodo: Object.prototype.hasOwnProperty.call(saved, "periodo")
        ? saved.periodo
        : saved.fechaDesde || saved.fechaHasta ? "custom" : "",
    };
  } catch (error) {
    return { ...initialFilters };
  }
}

function localDateKey(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function filterOffers(offers, filters, statuses, statusHistory) {
  const preset = filters.periodo && filters.periodo !== "custom"
    ? getOfferDateRange(filters.periodo)
    : null;
  const start = preset ? toInputDate(preset.start) : filters.periodo === "custom" ? filters.fechaDesde : "";
  const end = preset ? toInputDate(preset.end) : filters.periodo === "custom" ? filters.fechaHasta : "";
  const historyIndex = filters.dashboardMetric && filters.dashboardMetric !== "total" && statusHistory
    ? indexOfferStatusHistory(statusHistory)
    : null;
  return agruparOfertas(offers).filter((offer) => {
    if (filters.clienteId && offer.cliente_id !== filters.clienteId) return false;
    if (filters.estadoId && offer.estado_id !== filters.estadoId) return false;
    if (filters.dashboardMetric && filters.dashboardMetric !== "total" && !historyIndex) return false;
    const movement = historyIndex ? getLatestOfferStatusMovement(offer, historyIndex) : null;
    if (movement && getOfferDashboardType(movement, statuses) !== filters.dashboardMetric) return false;
    if (start || end) {
      const offerDate = localDateKey(movement ? movement.fecha : offer.fecha_alta);
      if (!offerDate) return false;
      if (start && offerDate < start) return false;
      if (end && offerDate > end) return false;
    }
    return true;
  });
}

function canEditOffer(offerStatus) {
  const normalizedStatusDescription =
    offerStatus?.descripcion?.trim().toLowerCase() || "";

  return (
    offerStatus?.id === OFFER_STATUS.PRESENTADA ||
    offerStatus?.id === OFFER_STATUS.BORRADOR ||
    offerStatus?.id === OFFER_STATUS.APROBADA ||
    normalizedStatusDescription === "denegada" ||
    normalizedStatusDescription === "cancelada"
  );
}

export default function OffersPage() {
  const history = useHistory();
  const location = useLocation();
  const [data, setData] = useState([]);
  const [selectedOffer, setSelectedOffer] = useState(null);

  const [customers, setCustomers] = useState([]);
  const [offerStatus, setOfferStatus] = useState([]);
  const [statusHistory, setStatusHistory] = useState(null);
  const [statusHistoryError, setStatusHistoryError] = useState(false);

  const [openConfirmDialog, setOpenConfirmDialog] = useState(null);

  const [refresh, setRefresh] = useState(false);

  const [collapsed, setCollapsed] = useState(true);
  const [filterOptions, setFilterOptions] = useState(() => readSavedFilters(location.state?.dashboardFilters));
  const filteredData = filterOffers(data, filterOptions, offerStatus, statusHistory);
  const dashboardFilterLoading = filterOptions.dashboardMetric && filterOptions.dashboardMetric !== "total"
    && !statusHistoryError && (!statusHistory || !offerStatus.length);

  const [revisionData, setRevisionData] = useState([]);
  const [
    openOfferRevisionTableDialog,
    setOpenOfferRevisionTableDialog,
  ] = useState(false);
  const [
    openDuplicarOfertaRevisionDialog,
    setOpenDuplicarOfertaRevisionDialog,
  ] = useState(false);

  const loggedUser = useSelector(
    (store) => store.authentication?.user,
    shallowEqual
  );
  const isOfferManager = loggedUser?.role?.rango === ROLES.GESTOR_OFERTAS;

  useEffect(() => {
    if (!location.state?.dashboardFilters) return;
    const nextState = { ...location.state };
    delete nextState.dashboardFilters;
    history.replace({ ...location, state: nextState });
  }, [history, location]);

  useEffect(() => {
    try {
      sessionStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(filterOptions));
    } catch (error) {
      // Filters remain usable without browser storage.
    }
  }, [filterOptions]);

  useEffect(() => {
    if (!filterOptions.dashboardMetric || filterOptions.dashboardMetric === "total") return undefined;
    let mounted = true;
    setStatusHistoryError(false);
    getOfferStatusHistory(loggedUser?.accessToken)
      .then((response) => {
        if (mounted) setStatusHistory(response.data || []);
      })
      .catch((error) => {
        if (!mounted) return;
        setStatusHistoryError(true);
        alertError({ error, customMessage: "No se ha podido aplicar el filtro del dashboard." });
      });
    return () => { mounted = false; };
  }, [filterOptions.dashboardMetric, loggedUser?.accessToken]);

  function handleEditOffer(offerId, revisionNum) {
    history.push({
      pathname: `/ofertas/${offerId}/${revisionNum}/edit`,
      state: {
        page: getCurrentTablePage(location),
      },
    });
  }

  function handleDuplicateOffer(offerId, revisionNum) {
    duplicateOfferHeader(offerId, revisionNum, loggedUser?.accessToken)
      .then((res) => {
        if (res.status === 200) {
          alertSuccess({
            title: "¡Duplicada!",
            customMessage: "Oferta duplicada con éxito.",
          });

          history.push({
            pathname: `/ofertas/${res.data?.id}/${res.data?.revision_num}/edit`,
            state: {
              page: getCurrentTablePage(location),
            },
          });
        }
      })
      .catch((error) => {
        alertError({
          error,
          customMessage: "No se ha podido duplicar la oferta.",
        });
      });
  }

  function buttonFormatter(key) {
    const offerId = key?.id;
    const revisionNum = key?.revisionNum;
    const revisionNums = key?.revisionNums;
    const offerStatus = key?.offerStatus;
    const elem = data.find(
      (item) => item.id === offerId && item.revision_num === revisionNum
    );
    return (
      <>
        {canEditOffer(offerStatus) && (
          <Tooltip title="Editar">
            <Button
              style={buttonsStyle}
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleEditOffer(offerId, revisionNum);
              }}
            >
              <EditIcon />
            </Button>
          </Tooltip>
        )}
        <Tooltip title="Eliminar">
          <Button
            style={buttonsStyle}
            size="small"
            disabled={isOfferManager}
            onClick={(e) => {
              e.stopPropagation();
              setSelectedOffer(elem);
              setOpenConfirmDialog(2);
            }}
          >
            <DeleteIcon />
          </Button>
        </Tooltip>
        <Tooltip title="Duplicar">
          <Button
            style={buttonsStyle}
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedOffer(elem);

              if (revisionNums) {
                setRevisionData(
                  data
                    .filter((x) => x.id === offerId)
                    .sort((a, b) => a.revision_num - b.revision_num)
                );
                setOpenDuplicarOfertaRevisionDialog(true);
                return;
              }

              setOpenConfirmDialog(3);
            }}
          >
            <FileCopyIcon />
          </Button>
        </Tooltip>
        {revisionNums && (
          <Tooltip title="Revisiones Editables">
            <Button
              style={buttonsStyle}
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setRevisionData(
                  // data.filter(
                  //   (x) =>
                  //     x.id === offerId &&
                  //     x.estado_id !== OFFER_STATUS.PRESENTADA
                  // )
                  data.filter((x) => x.id === offerId)

                );
                setOpenOfferRevisionTableDialog(true);
              }}
            >
              <ReviewsIcon />
            </Button>
          </Tooltip>
        )}
      </>
    );
  }

  const columns = [
    { dataField: "numOferta", text: "Número de oferta", sort: true },
    {
      dataField: "revisionNum",
      text: "Revisión",
      sort: true,
      headerAlign: "center",
      align: "center",
    },
    {
      dataField: "cliente",
      text: "Cliente",
      sort: true,
      headerAlign: "center",
      align: "center",
    },
    {
      dataField: "localizacion",
      text: "Localización",
      sort: true,
      headerAlign: "center",
      align: "center",
    },
    {
      dataField: "referencia",
      text: "Referencia de obra",
      sort: true,
      headerAlign: "center",
      align: "center",
    },
    {
      dataField: "estado",
      text: "Estado",
      sort: true,
      headerAlign: "center",
      align: "center",
      formatter: offerStatusFormatter,
    },
    { dataField: "key", text: "", formatter: buttonFormatter },
  ];

  const rowEvents = {
    onClick: (event, row) => {
      if (canEditOffer(row?.key?.offerStatus)) {
        handleEditOffer(row.key.id, row.key.revisionNum);
      }
    },
  };

  useEffect(() => {
    getCustomers(loggedUser?.accessToken)
      .then((res) => {
        if (res.status === 200) {
          setCustomers(res.data);
        }
      })
      .catch((error) => {
        alertError({
          error: error,
          customMessage: "No se han podido obtener los clientes.",
        });
      });
    getOfferStatus(loggedUser?.accessToken)
      .then((res) => {
        if (res.status === 200) {
          setOfferStatus(res.data);
        }
      })
      .catch((error) => {
        alertError({
          error: error,
          customMessage: "No se han podido obtener los estados.",
        });
      });
    getOfferHeaders(loggedUser?.accessToken)
      .then((res) => {
        if (res.status === 200) {
          setData(res.data);
          setRefresh(false);
        }
      })
      .catch((error) => {
        alertError({
          error: error,
          customMessage: "No se han podido obtener las ofertas.",
        });
      });
  }, [refresh]);

  const handleClearFilters = () => {
    updateFilters({ ...initialFilters });
  };

  const updateFilters = (nextFilters) => {
    setFilterOptions(nextFilters);
    if (getCurrentTablePage(location) > 1) {
      history.replace({ ...location, search: "?page=1" });
    }
  };

  const handlePeriodChange = (periodo) => {
    const nextFilters = { ...filterOptions, periodo };
    if (periodo === "custom" && !nextFilters.fechaDesde && !nextFilters.fechaHasta) {
      const today = new Date();
      nextFilters.fechaDesde = toInputDate(new Date(today.getFullYear(), today.getMonth(), 1));
      nextFilters.fechaHasta = toInputDate(today);
    }
    updateFilters(nextFilters);
  };

  const renderFiltersContent = () => {
    return (
      <>
        <div className="offers-filters">
          <Autocomplete
            id="autocomplete-customer"
            filterSelectedOptions
            options={customers}
            getOptionLabel={(option) => option?.nombre || ""}
            getOptionSelected={(option, value) => option.id === value.id}
            value={customers.find((item) => item.id === filterOptions.clienteId) || null}
            onChange={(event, selected) => {
              updateFilters({
                ...filterOptions,
                clienteId: selected?.id || "",
              });
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                margin="normal"
                variant="outlined"
                InputLabelProps={{
                  shrink: true,
                }}
                label="Cliente"
              />
            )}
          />
          <div className="offers-status-filters" role="group" aria-label="Filtrar por estado">
            <button
              type="button"
              className={`offers-status-filter offers-status-all ${!filterOptions.estadoId && !filterOptions.dashboardMetric ? "is-active" : ""}`}
              aria-pressed={!filterOptions.estadoId && !filterOptions.dashboardMetric}
              onClick={() => updateFilters({ ...filterOptions, estadoId: "", dashboardMetric: "" })}
            >
              Todas
            </button>
            {offerStatus.map((status) => {
              const appearance = getOfferStatusAppearance(status);
              const active = filterOptions.estadoId === status.id;
              return (
                <button
                  key={status.id}
                  type="button"
                  className={`offers-status-filter ${appearance.className} ${active ? "is-active" : ""}`}
                  style={appearance.style}
                  aria-pressed={active}
                  onClick={() => updateFilters({ ...filterOptions, estadoId: active ? "" : status.id, dashboardMetric: "" })}
                >
                  {status.descripcion}
                </button>
              );
            })}
          </div>
          <div className="offers-period-filters" role="group" aria-label={filterOptions.dashboardMetric && filterOptions.dashboardMetric !== "total" ? "Filtrar por fecha del último cambio de estado" : "Filtrar por fecha de alta"}>
            <Button className={!filterOptions.periodo ? "active" : ""} aria-pressed={!filterOptions.periodo} onClick={() => handlePeriodChange("")}>Todas</Button>
            {OFFER_DATE_PERIODS.map((period) => (
              <Button
                key={period.value}
                className={filterOptions.periodo === period.value ? "active" : ""}
                aria-pressed={filterOptions.periodo === period.value}
                onClick={() => handlePeriodChange(period.value)}
              >
                {period.label}
              </Button>
            ))}
          </div>
          {filterOptions.periodo === "custom" && (
            <div className="offers-date-filters">
              <label>
                Desde
                <input type="date" value={filterOptions.fechaDesde} max={filterOptions.fechaHasta || undefined} onChange={(event) => updateFilters({ ...filterOptions, fechaDesde: event.target.value })} />
              </label>
              <label>
                Hasta
                <input type="date" value={filterOptions.fechaHasta} min={filterOptions.fechaDesde || undefined} onChange={(event) => updateFilters({ ...filterOptions, fechaHasta: event.target.value })} />
              </label>
            </div>
          )}
        </div>
      </>
    );
  };

  return (
    <>
      <Card>
        <CardHeader headerClassName="px-0">
          <CardHeader
            title="Ofertas"
            sticky={false}
            headerClassName="d-flex justify-content-between w-100 py-3"
          >
            <CardHeaderToolbar>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() =>
                  history.push({
                    pathname: "/ofertas/edit",
                  })
                }
              >
                Nueva oferta
              </button>
            </CardHeaderToolbar>
          </CardHeader>
          <div className="w-100">
            <FiltersCard
              className="offers-filters-card"
              collapsable={false}
              filtersContent={renderFiltersContent}
              collapsed={collapsed}
              setCollapsed={setCollapsed}
              handleClearFilters={handleClearFilters}
              handleSearch={() => updateFilters({ ...filterOptions })}
            />
          </div>
        </CardHeader>
        <CardBody>
          {filterOptions.dashboardMetric && (
            <div className="offers-dashboard-filter-note">
              <span>
                Desde Visión Global: <strong>{dashboardMetricTitles[filterOptions.dashboardMetric]}</strong>
                <small>{filterOptions.dashboardMetric === "total" ? "Fecha de alta de la oferta" : "Fecha del último cambio de estado"}</small>
              </span>
              <Button onClick={() => updateFilters({ ...filterOptions, dashboardMetric: "" })}>Quitar filtro del dashboard</Button>
            </div>
          )}
          {dashboardFilterLoading ? (
            <div className="offers-dashboard-filter-loading"><CircularProgress size={24} /> Cargando ofertas del dashboard...</div>
          ) : statusHistoryError && filterOptions.dashboardMetric && filterOptions.dashboardMetric !== "total" ? (
            <div className="offers-dashboard-filter-error">No se ha podido cargar el historial de estados. Quita el filtro del dashboard para ver todas las ofertas.</div>
          ) : (
            <Table
              data={getData(filteredData, customers, offerStatus)}
              columns={columns}
              rowEvents={rowEvents}
              rowStyle={{ cursor: "pointer" }}
            />
          )}

          <ConfirmDialog
            title={"Â¿EstÃ¡s seguro de que quieres eliminar esta oferta?"}
            open={openConfirmDialog === 2}
            setOpen={setOpenConfirmDialog}
            onConfirm={() => {
              deleteOfferHeader(
                selectedOffer?.id,
                selectedOffer?.revision_num,
                loggedUser?.accessToken
              )
                .then((res) => {
                  if (res.status === 204 || res.status === 200) {
                    alertSuccess({
                      title: "Â¡Eliminada!",
                      customMessage: "Oferta eliminada con Ã©xito.",
                    });
                    setRefresh(true);
                  }
                })
                .catch((error) => {
                  alertError({
                    error: error,
                    customMessage: "No se ha podido eliminar la oferta.",
                  });
                });
            }}
          />
          <ConfirmDialog
            title={"¿Estás seguro de que quieres duplicar esta oferta?"}
            open={openConfirmDialog === 3}
            setOpen={setOpenConfirmDialog}
            onConfirm={() => {
              handleDuplicateOffer(
                selectedOffer?.id,
                selectedOffer?.revision_num
              );
            }}
          />
          <OfferRevisionTableDialog
            title={"Revisiones Editables"}
            open={openOfferRevisionTableDialog}
            setOpen={setOpenOfferRevisionTableDialog}
            data={revisionData}
            customers={customers}
            offerStatus={offerStatus}
          />
          <DuplicarOfertaRevisionDialog
            title={"Selecciona la revisión a duplicar"}
            open={openDuplicarOfertaRevisionDialog}
            setOpen={setOpenDuplicarOfertaRevisionDialog}
            data={revisionData}
            offerStatus={offerStatus}
            onConfirm={(selectedRevision) => {
              handleDuplicateOffer(
                selectedRevision?.id,
                selectedRevision?.revision_num
              );
            }}
          />
        </CardBody>
      </Card>
    </>
  );
}
